"use client";

import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import styles from "./page.module.css";
import Footer from "@agavian-ent/components/home/Footer";
import SummerFavourites from "@agavian-ent/components/home/SummerFavourites";
import Excellance from "@agavian-ent/components/home/Excellance";
import DoctorProfile from "@agavian-ent/components/home/DoctorProfile";
import VideoNewsletter from "@agavian-ent/components/home/VideoNewsletter";
import NavbarSection from "@package/components/theme-common/HomeDynamic/sections/NavbarSection";
import navbarConfig from "@package/util/config/navbarConfig.js";
import { APP_THEME } from "./theme-app-config";

const HERO_IMAGES = [
  {
    src: "/assets/images/w1200h800-Naveed Photo.avif",
    alt: "Mr Naveed in clinic",
  },
  {
    src: "/assets/images/w1200h800-DSC_4626.avif",
    alt: "ENT Central clinic moment",
  },
  {
    src: "/assets/images/8f975a8e9f6427e4d9a187535f9a635e.avif",
    alt: "ENT Central treatment room",
  },
  {
    src: "/assets/images/IMG_0038.jpg",
    alt: "ENT Central clinic interior",
  },
  {
    src: "/assets/images/IMG_0074.jpg",
    alt: "ENT Central treatment chair",
  },
];

const getHeroImage = (index) => HERO_IMAGES[index % HERO_IMAGES.length];

export default function HomeClient({ homeData = null }) {
  const [showIntro, setShowIntro] = useState(true);
  const [activeHeroIndex, setActiveHeroIndex] = useState(0);
  const introOverlayRef = useRef(null);
  const introMarkRef = useRef(null);
  const introLogoRef = useRef(null);
  const introEntLetterRefs = useRef([]);
  const introCentralLetterRefs = useRef([]);
  const heroContentRef = useRef(null);
  const heroHeadingRef = useRef(null);
  const heroTextRef = useRef(null);
  const leftHeroImage = getHeroImage(activeHeroIndex);
  const rightHeroImage = getHeroImage(activeHeroIndex + 1);

  useEffect(() => {
    if (!showIntro) return undefined;

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    window.scrollTo(0, 0);
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    const context = gsap.context(() => {
      gsap.set(introOverlayRef.current, { autoAlpha: 1 });
      gsap.set(introMarkRef.current, { scale: 0.92, y: 16 });
      gsap.set(introLogoRef.current, { autoAlpha: 0 });
      gsap.set(introEntLetterRefs.current, { autoAlpha: 0, y: 28 });
      gsap.set(introCentralLetterRefs.current, { autoAlpha: 0, y: 28 });

      const timeline = gsap.timeline({
        defaults: { ease: "power3.out" },
        onComplete: () => {
          window.scrollTo(0, 0);
          setShowIntro(false);
        },
      });

      timeline
        .to(introLogoRef.current, {
          autoAlpha: 1,
          duration: 0.85,
        })
        .to(
          introMarkRef.current,
          {
            scale: 1.1,
            y: 0,
            duration: 2.7,
            ease: "power1.inOut",
          },
          "<"
        )
        .to(
          [...introEntLetterRefs.current, ...introCentralLetterRefs.current],
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.56,
            stagger: 0.09,
          },
          "-=1.7"
        )
        .to(introOverlayRef.current, {
          autoAlpha: 0,
          duration: 0.65,
          ease: "power2.inOut",
        });
    }, introOverlayRef);

    return () => {
      context.revert();
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
      window.scrollTo(0, 0);
    };
  }, [showIntro]);

  useEffect(() => {
    const context = gsap.context(() => {
      gsap.set([heroHeadingRef.current, heroTextRef.current], {
        autoAlpha: 0,
        y: 34,
        filter: "blur(8px)",
      });

      gsap
        .timeline({ defaults: { ease: "power3.out" } })
        .to(heroHeadingRef.current, {
          autoAlpha: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 1,
        })
        .to(
          heroTextRef.current,
          {
            autoAlpha: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.9,
          },
          "-=0.55"
        );
    }, heroContentRef);

    return () => context.revert();
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveHeroIndex((currentIndex) => (currentIndex + 1) % HERO_IMAGES.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, []);

  const handleHeroSlide = (direction) => {
    setActiveHeroIndex((currentIndex) => {
      const nextIndex = currentIndex + direction;
      return (nextIndex + HERO_IMAGES.length) % HERO_IMAGES.length;
    });
  };

  return (
    <div className={styles.container}>
      {showIntro ? (
        <div
          className={styles.introOverlay}
          ref={introOverlayRef}
          aria-label="ENT Central"
          aria-live="polite"
        >
          <div className={styles.introMark} ref={introMarkRef}>
            <img
              className={styles.introLogo}
              ref={introLogoRef}
              src="/assets/Icons/entlogohight.png"
              alt="ENT Central logo"
            />
            <div className={styles.introTitle} aria-hidden="true">
              <span className={styles.introEnt}>
                {["E", "N", "T"].map((letter, index) => (
                  <span
                    className={styles.introLetter}
                    key={letter}
                    ref={(node) => {
                      introEntLetterRefs.current[index] = node;
                    }}
                  >
                    {letter}
                  </span>
                ))}
              </span>
              <span className={styles.introCentral}>
                {"Central".split("").map((letter, index) => (
                  <span
                    className={styles.introLetter}
                    key={`${letter}-${index}`}
                    ref={(node) => {
                      introCentralLetterRefs.current[index] = node;
                    }}
                  >
                    {letter}
                  </span>
                ))}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      <NavbarSection
        config={navbarConfig.navbarConfig}
        theme={APP_THEME}
        websiteDetails={homeData?.websiteDetails}
        isEcommerceEnabled
        companyDetails={homeData?.companyDetails}
      />

      <main className={styles.main}>
        <section id="home" className={styles.hero}>
          <div className={styles.heroSide}>
            <img
              src={leftHeroImage.src}
              alt={leftHeroImage.alt}
              className={styles.sideImage}
            />
          </div>

          <div className={styles.heroContent} ref={heroContentRef}>
            <h1 className={styles.heroHeading} ref={heroHeadingRef}>
              Welcome to ENT Central
            </h1>
            <p className={styles.heroText} ref={heroTextRef}>
              ENT Central offers a comprehensive service to treat a wide range of
              ear, nose and throat conditions. We use the most advanced
              techniques in the examination, investigation and management
              strategies collating experience from top institutions in the world
              and bringing it to New Zealand for the first time.
            </p>
          </div>

          <div className={styles.heroSide}>
            <img
              src={rightHeroImage.src}
              alt={rightHeroImage.alt}
              className={styles.sideImage}
            />
          </div>

          <button
            className={styles.heroArrowLeft}
            type="button"
            aria-label="Previous slide"
            onClick={() => handleHeroSlide(-1)}
          >
            &lsaquo;
          </button>
          <button
            className={styles.heroArrowRight}
            type="button"
            aria-label="Next slide"
            onClick={() => handleHeroSlide(1)}
          >
            &rsaquo;
          </button>
        </section>

        <SummerFavourites
          products={homeData?.productDetails}
          productBaseUrl={homeData?.productBaseUrl}
        />
        <DoctorProfile />
        <Excellance />
        <VideoNewsletter />
      </main>

      <Footer />
    </div>
  );
}
